import moment from 'moment';

/**
 * Turns per-day reading counts into a picture of what a period actually
 * captured.
 *
 * Consumption totals cannot show this. A day the meter never reported and a day
 * it reported zero flow both sum to zero, so a month missing a week reads the
 * same as a quiet one — and on this data a meter dark for eight days looked
 * healthy. Counting readings is the only thing that separates them.
 */

/** How a day is classified. */
export const DayState = { FULL: 'full', PARTIAL: 'partial', DARK: 'dark' };

/** A day below this share of a normal one is treated as partial. */
const PARTIAL_THRESHOLD = 0.9;

/**
 * How a day's volume compares with a normal one on this meter.
 *
 * NONE is not the bottom of the scale. A day the meter never reported has no
 * volume to place, and colouring it as the lowest would make a dead gateway
 * look like a quiet Sunday — which is the confusion this whole panel exists to
 * prevent. Meters that report a rate rather than a running total have no
 * volume either, and every one of their days is NONE.
 */
export const VolumeBand = {
  HIGH: 'high',
  USUAL: 'usual',
  LOW: 'low',
  NONE: 'none',
};

/** Above this share of a normal day is high, below the other is low. */
const HIGH_RATIO = 1.5;
const LOW_RATIO = 0.5;

/**
 * The average of a list, used as the window's own idea of a normal day.
 *
 * Only the days that reported are averaged. A gap is an absent measurement
 * rather than a day of no water, and counting it as zero would pull the
 * average down until ordinary days started reading as heavy ones.
 *
 * @param {Array<number>} values - Volumes for the days that reported.
 * @returns {number} The mean, or 0 when there is nothing to average.
 */
export const meanOf = (values = []) => {
  const measured = values.filter((value) => Number.isFinite(value));
  if (!measured.length) return 0;

  return measured.reduce((total, value) => total + value, 0) / measured.length;
};

/**
 * Places a day's volume against a normal one.
 *
 * Normal is the average across the thirty days on the strip. A threshold in
 * cubic metres would be wrong for every meter but the one it was written for,
 * and wrong for that one next season.
 *
 * @param {?number} consumption - The day's volume, or null when there is none.
 * @param {number} normal - Average volume across the window's reporting days.
 * @returns {string} A VolumeBand.
 */
const bandFor = (consumption, normal) => {
  if (consumption === null || consumption === undefined) return VolumeBand.NONE;
  if (!normal) return VolumeBand.USUAL;

  if (consumption > normal * HIGH_RATIO) return VolumeBand.HIGH;
  if (consumption < normal * LOW_RATIO) return VolumeBand.LOW;

  return VolumeBand.USUAL;
};

/**
 * The middle value of a list, used as the window's own idea of a normal day.
 *
 * A fixed expectation would be wrong: this meter reported roughly 17,000 times
 * a day in June and 1,385 a day in July, so what counts as normal has to come
 * from the window being shown.
 *
 * @param {Array<number>} values - Counts for the days that reported.
 * @returns {number} The median, or 0 when there is nothing to measure.
 */
export const medianOf = (values = []) => {
  const sorted = values.filter((v) => v > 0).sort((a, b) => a - b);
  if (!sorted.length) return 0;

  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

/**
 * Reads a figure the endpoint may legitimately have left out.
 *
 * A meter that reports a rate rather than a running total has no consumption
 * to give, and the endpoint sends null for it. `Number(null)` is 0, which
 * would put a confident "0" against every day of such a meter instead of
 * falling back to the reading count.
 *
 * @param {*} value - The raw field.
 * @returns {?number} The number, or null when there is not one.
 */
const numberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Builds one entry per calendar day across a window, whether or not the meter
 * reported.
 *
 * The endpoint returns only days that recorded something, so absent days are
 * the outages and have to be filled in here.
 *
 * A missing window is an error, not an empty one. `moment(undefined)` is a
 * valid moment at the current time, so a failed request — which arrives here
 * as undefined bounds — used to produce a single day of today, find no
 * readings in it, and report "0% uptime" with a full red bar. That is the
 * panel stating a fact about the meter when what actually happened is that
 * the request failed.
 *
 * @param {Array<{day: string, count: number, consumption: ?number}>} rows - Days
 *   from the endpoint.
 * @param {string} from - Start of the window.
 * @param {string} to - End of the window.
 * @returns {Array<{date: string, label: string, count: number, consumption: ?number, state: string}>}
 */
export const buildCoverage = (rows = [], from, to) => {
  if (!from || !to) return [];

  const reported = new Map(
    (Array.isArray(rows) ? rows : []).map((row) => [row.day, row])
  );

  const start = moment(from).startOf('day');
  const end = moment(to).startOf('day');
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return [];

  const normal = medianOf(
    Array.from(reported.values()).map((row) => Number(row.count) || 0)
  );

  const usualVolume = meanOf(
    Array.from(reported.values())
      .map((row) => numberOrNull(row.consumption))
      .filter((volume) => volume !== null)
  );

  const days = [];

  for (const at = start.clone(); at.isSameOrBefore(end); at.add(1, 'day')) {
    const date = at.format('YYYY-MM-DD');
    const row = reported.get(date);
    const count = Number(row?.count) || 0;
    const consumption = count ? numberOrNull(row?.consumption) : null;

    days.push({
      date,
      label: at.format('DD MMM'),
      count,
      consumption,
      band: count ? bandFor(consumption, usualVolume) : VolumeBand.NONE,
      state:
        count === 0
          ? DayState.DARK
          : normal && count < normal * PARTIAL_THRESHOLD
            ? DayState.PARTIAL
            : DayState.FULL,
    });
  }

  return days;
};

/**
 * Summarises a window's coverage for the heading.
 * @param {Array<object>} days - Output of buildCoverage.
 * @returns {{total: number, dark: number, partial: number, whole: boolean}}
 */
export const summariseCoverage = (days = []) => {
  const total = days.length;
  const dark = days.filter((day) => day.state === DayState.DARK).length;
  const partial = days.filter((day) => day.state === DayState.PARTIAL).length;

  return {
    total,
    dark,
    partial,
    whole: total > 0 && dark === 0 && partial === 0,
  };
};

/**
 * Whether this meter gives volumes to colour by.
 *
 * A meter reporting a rate has no running total to difference, so the endpoint
 * sends no consumption and there is nothing to shade. Those meters keep the
 * coverage colouring, which is the only thing their data supports.
 *
 * @param {Array<object>} days - Output of buildCoverage.
 * @returns {boolean} True when at least one day has a volume.
 */
export const hasVolume = (days = []) =>
  days.some((day) => day.consumption !== null && day.consumption !== undefined);

/**
 * Formats a volume for reading rather than for arithmetic.
 * @param {number} value - The figure.
 * @returns {string} The magnitude, to at most two decimals.
 */
const volume = (value) =>
  Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * What one day of the strip says when the reader points at it.
 *
 * The volume and the reading count, and nothing else. How the day compares
 * with a usual one used to be spelled out here too, which is the one thing the
 * square's own shade already says — a tooltip that reads the colour back to
 * the reader is a sentence they did not need to hover for.
 *
 * The count stays because the shade cannot carry it: it says a day was light
 * without saying whether that meant four readings or ninety, which is the
 * difference between a meter worth investigating and one that missed an hour.
 *
 * A total that fell is stated as a fall rather than as negative consumption:
 * these are odometers, and one running backwards is a fault to name, not a
 * quantity of water to report.
 *
 * @param {object} day - One entry from buildCoverage.
 * @param {string} unit - The meter's unit, blank when it has none.
 * @returns {string} A phrase for the square's tooltip.
 */
export const describeDay = (day, unit) => {
  if (!day) return '';

  const suffix = unit ? ` ${unit}` : '';
  const readings = `${day.count?.toLocaleString()} reading${day.count === 1 ? '' : 's'}`;

  if (!day.count) return `${day.label} — no readings`;
  if (day.consumption === null || day.consumption === undefined) {
    return `${day.label} — ${readings}`;
  }
  if (day.consumption < 0) {
    return `${day.label} — meter total fell by ${volume(day.consumption)}${suffix} · ${readings}`;
  }

  return `${day.label} — ${volume(day.consumption)}${suffix} consumed · ${readings}`;
};

/**
 * States plainly what the window is missing.
 * @param {object} summary - Output of summariseCoverage.
 * @returns {string} A sentence for the section heading.
 */
export const describeCoverage = (summary) => {
  if (!summary?.total) return 'No reading history for this period.';
  if (summary.whole) return '';

  const days = (count) => `${count} ${count === 1 ? 'day' : 'days'}`;
  const parts = [];

  if (summary.dark) {
    parts.push(`${days(summary.dark)} reported nothing`);
  }
  if (summary.partial) {
    parts.push(`${days(summary.partial)} reported only partly`);
  }

  return `${parts.join(' and ')}, so the totals here are understated.`;
};

/**
 * How much of the window the meter reported in, for the line beside the count.
 *
 * A percentage of uptime read as the panel's headline back when the colours
 * carried coverage. They carry volume now, so this is a caveat rather than a
 * verdict, and days out of days says the same thing without the framing.
 *
 * It is still the only place coverage is stated: a day with four readings and
 * a genuinely light day are both pale green, so the shading cannot say which
 * is which.
 *
 * @param {object} summary - Output of summariseCoverage.
 * @returns {string} A phrase, or an empty string when there is no window.
 */
export const describeReported = (summary) => {
  if (!summary?.total) return '';
  if (summary.whole) return 'Every day reported';

  return `${summary.total - summary.dark} of ${summary.total} days reported`;
};
